import type { ValidationIssues } from '~/models/common'
import {
  applyDomainGoalTransition,
  buildDomainGoalRecord,
  type DomainChangeSource,
  type DomainGoalInput,
  type DomainGoalRecord,
  type DomainIntention,
  type DomainPriority,
  type DomainStatus,
  type FitnessDomain,
  isParticipating,
  type WeeklyCommitment,
} from '~/models/fitnessDomain'
import type { WeeklySchedule } from '~/models/trainingSchedule'
import type { CalendarDate } from '~/types/dates'
import { toIsoTimestamp } from '~/types/dates'
import type { DomainGoalId, ProjectId } from '~/types/ids'
import type { Result } from '~/utils/result'
import { err, ok } from '~/utils/result'
import type { SolaDatabase } from '../db'
import { type StorageError, storageError, toStorageError } from '../errors'
import { notifyWritten } from '../invalidation'
import { STORE } from '../schema'
import { withTransaction } from '../transactions'

/**
 * Domain goals (D-090).
 *
 * Reads and single-record lifecycle writes. **Creating the initial set is not
 * here**, for the same reason `projectRepository` is read-only: setup writes the
 * profile, project, phase, body-composition target, measurement session *and*
 * every domain goal in one atomic transaction, so that path belongs to a service
 * holding the whole transaction rather than to a repository writing one record.
 * A domain goal created on its own afterwards — the user adding cardio in week
 * six — is a single write and does live here.
 */

export async function getDomainGoal(
  db: SolaDatabase,
  id: DomainGoalId,
): Promise<Result<DomainGoalRecord | undefined, StorageError>> {
  try {
    return ok(await db.get(STORE.domainGoals, id))
  } catch (cause) {
    return err(toStorageError(cause))
  }
}

export async function listDomainGoals(
  db: SolaDatabase,
  projectId: ProjectId,
): Promise<Result<readonly DomainGoalRecord[], StorageError>> {
  try {
    const goals = await db.getAllFromIndex(STORE.domainGoals, 'projectId', projectId)
    return ok(sortForDisplay(goals))
  } catch (cause) {
    return err(toStorageError(cause))
  }
}

/**
 * The goals that take part in tasks, evidence and the weekly review.
 *
 * Filtered in memory rather than by index. The `status` index cannot express
 * this question on its own — `isParticipating` is status **and** intention, and a
 * record can be `active` with an `inactive` intention — and a project holds at
 * most four domain goals, so the filter is over four records. Adding a second
 * indexed flag to make one query out of two conditions is exactly the
 * speculative index D-027 forbids.
 */
export async function listParticipatingDomainGoals(
  db: SolaDatabase,
  projectId: ProjectId,
): Promise<Result<readonly DomainGoalRecord[], StorageError>> {
  const all = await listDomainGoals(db, projectId)
  if (!all.ok) return all
  return ok(all.value.filter(isParticipating))
}

/**
 * Whether one domain is participating, for a screen deciding what to show.
 *
 * Returns `false` for a domain with no record, which is the normal way a domain
 * is off — an absent record is an inactive domain (see `backfillDomains.ts`).
 */
export async function isDomainActive(
  db: SolaDatabase,
  projectId: ProjectId,
  domain: FitnessDomain,
): Promise<Result<boolean, StorageError>> {
  const participating = await listParticipatingDomainGoals(db, projectId)
  if (!participating.ok) return participating
  return ok(participating.value.some((goal) => goal.domain === domain))
}

export async function createDomainGoal(
  db: SolaDatabase,
  input: DomainGoalInput,
  now: Date,
): Promise<Result<DomainGoalRecord, StorageError>> {
  const built = buildDomainGoalRecord(input, toIsoTimestamp(now))
  if (!built.ok) return err(invalid(built.error))

  try {
    /* One live goal per domain per project. A second `improve cardio` alongside
       an existing one would make "the cardio goal" ambiguous everywhere, and the
       user's intent when adding it is to replace, which is a decision they make
       explicitly by ending the first. */
    const existing = await db.getAllFromIndex(STORE.domainGoals, 'projectId', built.value.projectId)
    const clash = existing.find(
      (goal) => goal.domain === built.value.domain && goal.status !== 'ended',
    )
    if (clash !== undefined) {
      return err(
        storageError(
          'invalidRecord',
          'There is already a goal for that area. End it first, or change the one that is there.',
        ),
      )
    }

    await db.put(STORE.domainGoals, built.value)
    notifyWritten(STORE.domainGoals)
    return ok(built.value)
  } catch (cause) {
    return err(toStorageError(cause))
  }
}

/**
 * Edit an existing domain goal in place.
 *
 * The stored record supplies the identity, so an edit can neither mint a second
 * goal nor change its **status, priority, intention or weekly commitment** — all
 * four move only through `transitionDomainGoal`, which records the change. An
 * edit that could also set them would move a goal's priority with no entry in
 * its history, which is the whole point of the append-only list. A goal that no
 * longer exists is a typed failure rather than a silent insert.
 */
export async function updateDomainGoal(
  db: SolaDatabase,
  id: DomainGoalId,
  input: DomainGoalInput,
  now: Date,
): Promise<Result<DomainGoalRecord, StorageError>> {
  try {
    const existing = await db.get(STORE.domainGoals, id)
    if (existing === undefined) {
      return err(storageError('invalidRecord', 'That goal no longer exists.'))
    }
    if (existing.status === 'ended') {
      return err(
        storageError(
          'invalidRecord',
          'That goal has ended. Add a new goal for this area instead of reopening it.',
        ),
      )
    }

    const built = buildDomainGoalRecord(input, toIsoTimestamp(now), {
      id: existing.id,
      createdAt: existing.createdAt,
      status: existing.status,
      priority: existing.priority,
      intention: existing.intention,
      ...(existing.weeklyCommitment === undefined
        ? {}
        : { weeklyCommitment: existing.weeklyCommitment }),
      changes: existing.changes,
      ...(existing.endedOn === undefined ? {} : { endedOn: existing.endedOn }),
    })
    if (!built.ok) return err(invalid(built.error))

    await db.put(STORE.domainGoals, built.value)
    notifyWritten(STORE.domainGoals)
    return ok(built.value)
  } catch (cause) {
    return err(toStorageError(cause))
  }
}

/**
 * Pause, resume, re-prioritise or end a domain goal, recording the change.
 *
 * A transition that changes nothing writes nothing and appends nothing, so the
 * history shows changes that actually happened — the same rule as a phase-history
 * form saved unchanged. **There is no transition out of `ended`**: the model
 * refuses it, because reopening would be a new goal with its own start date.
 */
export async function transitionDomainGoal(
  db: SolaDatabase,
  id: DomainGoalId,
  next: {
    readonly status?: DomainStatus
    readonly priority?: DomainPriority
    readonly intention?: DomainIntention
    readonly weeklyCommitment?: WeeklyCommitment
  },
  now: Date,
  options: {
    readonly endedOn?: CalendarDate
    readonly effectiveFrom?: CalendarDate
    readonly reason?: string
    /** Whose numbers these were, when not the user's own (D-093 §6). */
    readonly source?: DomainChangeSource
  } = {},
): Promise<Result<DomainGoalRecord, StorageError>> {
  try {
    const existing = await db.get(STORE.domainGoals, id)
    if (existing === undefined) {
      return err(storageError('invalidRecord', 'That goal no longer exists.'))
    }

    const moved = applyDomainGoalTransition(existing, next, toIsoTimestamp(now), options)
    if (!moved.ok) return err(invalid(moved.error))

    // Nothing changed. Do not write, and do not report a change that did not happen.
    if (moved.value === existing) return ok(existing)

    await db.put(STORE.domainGoals, moved.value)
    notifyWritten(STORE.domainGoals)
    return ok(moved.value)
  } catch (cause) {
    return err(toStorageError(cause))
  }
}

/** One goal's part of a multi-goal save. */
export interface DomainGoalTransition {
  readonly id: DomainGoalId
  readonly next: {
    readonly status?: DomainStatus
    readonly priority?: DomainPriority
    readonly intention?: DomainIntention
    readonly weeklyCommitment?: WeeklyCommitment
    readonly weeklySchedule?: WeeklySchedule
  }
  readonly options?: {
    readonly endedOn?: CalendarDate
    readonly effectiveFrom?: CalendarDate
    readonly reason?: string
    readonly source?: DomainChangeSource
  }
}

/**
 * Transition several goals at once, or none of them (D-105 §7).
 *
 * ────────────────────────────────────────────────────────────────────────────
 *  A MULTI-DOMAIN SAVE MUST NEVER LEAVE HALF THE SCHEDULE UPDATED
 * ────────────────────────────────────────────────────────────────────────────
 *
 *  My Plan composes every participating domain into one chronological editor, so
 *  a single save can move a session from one domain goal to another. Written one
 *  record at a time, a failure between the two writes leaves the session on
 *  **both** goals or on **neither** — a schedule the user never arranged, with no
 *  error to explain it and nothing to roll back to.
 *
 *  So every record is read and every transition applied **before** the
 *  transaction opens, and the writes happen together inside it. Nothing in the
 *  callback awaits anything but IndexedDB; awaiting any other promise would
 *  commit it early and write half of this (see `transactions.ts`).
 *
 *  **A failure anywhere means nothing is written**, including the goals whose own
 *  transitions were perfectly valid.
 */
export async function transitionDomainGoals(
  db: SolaDatabase,
  transitions: readonly DomainGoalTransition[],
  now: Date,
): Promise<Result<readonly DomainGoalRecord[], StorageError>> {
  if (transitions.length === 0) return ok([])

  const timestamp = toIsoTimestamp(now)
  const moved: DomainGoalRecord[] = []

  try {
    for (const transition of transitions) {
      const existing = await db.get(STORE.domainGoals, transition.id)
      if (existing === undefined) {
        return err(storageError('invalidRecord', 'That goal no longer exists.'))
      }

      const applied = applyDomainGoalTransition(
        existing,
        transition.next,
        timestamp,
        transition.options ?? {},
      )
      if (!applied.ok) return err(invalid(applied.error))

      // Unchanged goals are dropped here rather than written and compared later:
      // a save that touched one domain must not append a history entry to three.
      if (applied.value !== existing) moved.push(applied.value)
    }
  } catch (cause) {
    return err(toStorageError(cause))
  }

  if (moved.length === 0) return ok([])

  const written = await withTransaction(db, [STORE.domainGoals] as const, async (tx) => {
    const store = tx.objectStore(STORE.domainGoals)
    /* Every request is issued before any is awaited, so the transaction sees
       them in one turn and cannot auto-commit between them. Each rejection is
       marked handled as it is issued: when one write fails the transaction
       aborts, which rejects its siblings too, and `Promise.all` surfaces only
       the first — the rest would otherwise escape as unhandled rejections. */
    const writes = moved.map((record) => {
      const request = store.put(record)
      void request.catch(() => undefined)
      return request
    })
    await Promise.all(writes)
  })
  if (!written.ok) return err(written.error)

  notifyWritten(STORE.domainGoals)
  return ok(moved)
}

/**
 * Order for display: primary, then supporting, then protect, then the rest, and
 * within a tier by the domain's own declaration order.
 *
 * The comparison is on the vocabulary arrays rather than on a stored rank, so
 * adding a priority or a domain reorders every screen at once and cannot leave
 * one list sorted by an older rule.
 */
const PRIORITY_ORDER: readonly DomainPriority[] = ['primary', 'supporting', 'protect', 'none']
const DOMAIN_ORDER: readonly FitnessDomain[] = ['bodyComposition', 'cardio', 'strength', 'mobility']

function sortForDisplay(goals: readonly DomainGoalRecord[]): readonly DomainGoalRecord[] {
  return [...goals].sort((left, right) => {
    const byPriority =
      PRIORITY_ORDER.indexOf(left.priority) - PRIORITY_ORDER.indexOf(right.priority)
    if (byPriority !== 0) return byPriority
    return DOMAIN_ORDER.indexOf(left.domain) - DOMAIN_ORDER.indexOf(right.domain)
  })
}

/* The issues are carried as the cause, not just flattened into the message, so a
   form can still point at the field that failed. */
function invalid(issues: ValidationIssues): StorageError {
  return storageError('invalidRecord', issues.map((entry) => entry.message).join(' '), issues)
}
